import { UserPreferenceKey } from "../../../../common/api/decimal/methods/user.ts";
import useUserPreference from "./useUserPreference.ts";
import useUserPreferenceDate from "./useUserPreferenceDate.ts";
import { useCallback } from "react";
import {
  FeatureName,
  useRootLoaderFeatureEnabled,
} from "../../../root/rootLoader.ts";

interface DismissibleFeatureProps {
  preferenceKey: UserPreferenceKey;
  suppressionMonths: number;
  isFeatureAvailable: boolean;
}

/**
 * Hook for features that can be dismissed and re-shown after a suppression period.
 * Follows the same pattern as useTrackNewFeature but for dismissible content.
 *
 * @param preferenceKey - UserPreferenceKey to store dismissal state
 * @param suppressionMonths - How many months to wait before re-showing
 * @param enabledConditions - Array of conditions that must be true to show
 * @returns Object with shouldShow flag and dismiss function
 */
export default function useDismissibleFeature({
  preferenceKey,
  suppressionMonths,
  isFeatureAvailable,
}: DismissibleFeatureProps) {
  const [dismissedValue, setDismissed, deleteDismissed] =
    useUserPreference<boolean>(preferenceKey);
  const shouldUseShortDurations = useRootLoaderFeatureEnabled(
    FeatureName.ShortDismissibleFeatures
  );

  // Convert months to days, use short duration for QA testing
  const suppressionDays = shouldUseShortDurations ? 1 : suppressionMonths * 30;

  // Check if suppression period has expired based on when preference was last updated
  const isPastSuppressionPeriod = useUserPreferenceDate(
    preferenceKey,
    suppressionDays
  );
  const dismiss = useCallback(() => {
    setDismissed(true); // Value doesn't matter, updatedAt timestamp is what counts
  }, [setDismissed]);

  const clearDismissal = useCallback(() => {
    deleteDismissed();
  }, [deleteDismissed]);

  // Show if: conditions met AND (never dismissed OR suppression period expired)
  const shouldShow =
    isFeatureAvailable && (!dismissedValue || isPastSuppressionPeriod);

  return {
    shouldShow,
    dismiss,
    clearDismissal,
    isDismissed: Boolean(dismissedValue),
    isPastSuppressionPeriod,
  };
}
